/**
 * Portal Component for Bug Reporter Widget
 *
 * Renders children outside Shadow DOM into document.body.
 * This is necessary for full-screen overlays like ScreenshotCanvas
 * that need to be above all other elements including those on the host page.
 *
 * @module portal
 */

import { useEffect, useState, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
  /** Content to render in the portal */
  children: ReactNode;
  /** Optional container ID (default: bug-reporter-portal) */
  containerId?: string;
}

/**
 * Portal component that renders content outside Shadow DOM
 *
 * Creates a container directly in document.body with maximum z-index
 * to ensure content appears above all host page elements.
 *
 * Features:
 * - Creates container in document.body (outside Shadow DOM)
 * - Uses maximum z-index (2147483647)
 * - Cleans up container on unmount
 * - Injects necessary styles for canvas
 *
 * @example
 * ```tsx
 * <Portal>
 *   <ScreenshotCanvas ... />
 * </Portal>
 * ```
 */
export const Portal: React.FC<PortalProps> = ({
  children,
  containerId = 'bug-reporter-portal',
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    // Check if container already exists (from previous render)
    let portalContainer = document.getElementById(containerId);
    const stylesId = `${containerId}-styles`;

    if (!portalContainer) {
      // Create new container in document.body
      portalContainer = document.createElement('div');
      portalContainer.id = containerId;
      portalContainer.setAttribute('data-bug-reporter-portal', 'true');

      // Apply full-viewport positioning with maximum z-index
      // Use width/height: 100% instead of 0 to avoid visibility issues
      // in browsers where overflow:visible + fixed children can be clipped
      portalContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 2147483647;
        pointer-events: none;
        overflow: visible;
      `;

      document.body.appendChild(portalContainer);
    }

    // ALWAYS ensure styles are injected (even if container existed)
    // This fixes the bug where styles were removed on unmount but container persisted
    let styleElement = document.getElementById(stylesId) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = stylesId;
      styleElement.textContent = getPortalStyles();
      document.head.appendChild(styleElement);
    }
    styleRef.current = styleElement;

    setContainer(portalContainer);

    // Cleanup on unmount
    return () => {
      if (portalContainer && portalContainer.parentNode) {
        // Only remove if no children
        if (portalContainer.childNodes.length === 0) {
          portalContainer.parentNode.removeChild(portalContainer);
        }
      }
      // Remove styles only if container was also removed (no more portal users)
      if (!document.getElementById(containerId)) {
        if (styleRef.current && styleRef.current.parentNode) {
          styleRef.current.parentNode.removeChild(styleRef.current);
          styleRef.current = null;
        }
      }
    };
  }, [containerId]);

  if (!container) {
    return null;
  }

  return createPortal(children, container);
};

/**
 * Get CSS styles for portal content
 * These styles are injected into the main document to style portal content
 */
function getPortalStyles(): string {
  return `
    /* Bug Reporter Portal Container */
    #bug-reporter-portal > * {
      pointer-events: auto !important;
    }

    /* Screenshot Canvas in Portal - Full screen overlay */
    #bug-reporter-portal .bug-reporter-screenshot-canvas {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 2147483647 !important;
      background-color: #000000;
      display: flex;
      flex-direction: column;
    }

    /* Toolbar in Portal */
    #bug-reporter-portal .bug-reporter-screenshot-toolbar {
      position: absolute !important;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 8px;
      padding: 12px;
      background-color: rgba(255, 255, 255, 0.95);
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      /* z-index higher than canvas (10) to be above it */
      z-index: 100 !important;
      cursor: grab;
      touch-action: none;
      flex-wrap: wrap;
      max-width: calc(100vw - 32px);
    }

    /* Fabric.js canvas wrapper - ensure it receives pointer events */
    /* Z-index must be LOWER than toolbar (2147483647) so toolbar stays on top of drawings */
    #bug-reporter-portal [data-fabric="wrapper"] {
      position: relative !important;
      user-select: none !important;
      touch-action: none !important;
      z-index: 2147483640 !important;
    }

    #bug-reporter-portal [data-fabric="wrapper"] canvas {
      touch-action: none !important;
      z-index: 2147483640 !important;
    }

    /* Lower canvas (background) */
    #bug-reporter-portal .lower-canvas,
    #bug-reporter-portal [data-fabric="main"] {
      z-index: 2147483639 !important;
    }

    /* Upper canvas must receive pointer events */
    /* z-index lower than toolbar (100) to not block toolbar clicks */
    #bug-reporter-portal .upper-canvas,
    #bug-reporter-portal [data-fabric="top"] {
      cursor: crosshair !important;
      pointer-events: auto !important;
      z-index: 10 !important;
    }

    /* Tool buttons */
    #bug-reporter-portal .bug-reporter-tool-btn {
      min-width: 36px;
      min-height: 36px;
      touch-action: manipulation;
      padding: 8px;
      border-radius: 4px;
      background-color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    #bug-reporter-portal .bug-reporter-tool-btn.active {
      border: 2px solid #3b82f6;
      background-color: #eff6ff;
    }

    /* Mobile responsive toolbar */
    @media (max-width: 768px) {
      #bug-reporter-portal .bug-reporter-screenshot-toolbar {
        top: 8px !important;
        padding: 8px !important;
        gap: 4px !important;
        max-width: calc(100vw - 16px);
      }

      #bug-reporter-portal .bug-reporter-tool-btn {
        padding: 6px !important;
        min-width: 32px;
        min-height: 32px;
      }

      #bug-reporter-portal .bug-reporter-tool-btn svg {
        width: 16px !important;
        height: 16px !important;
      }
    }

    @media (max-width: 480px) {
      #bug-reporter-portal .bug-reporter-screenshot-toolbar {
        flex-wrap: wrap;
        justify-content: center;
        gap: 6px !important;
        padding: 10px !important;
      }

      #bug-reporter-portal .bug-reporter-tool-btn {
        padding: 8px !important;
        min-width: 36px;
        min-height: 36px;
      }

      #bug-reporter-portal .bug-reporter-tool-btn svg {
        width: 18px !important;
        height: 18px !important;
      }
    }
  `;
}

export default Portal;
